import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { allFailed, removeFailed, addOp } from "@/lib/offlineQueue";
import { flushQueue } from "@/lib/offlineSync";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const LABELS = {
  tickets: "Ticket", defects: "Mangel", comments: "Kommentar", quantities: "Menge",
  files: "Datei", photos: "Foto", positions: "Position", inspections: "Prüfung",
  insp_assets: "Anlage", insp_categories: "Kategorie", insp_measures: "Maßnahme",
  __insp_replay__: "Prüf-Aktion", __media__: "Medien",
};
const OPS = { create: "Erstellen", update: "Ändern", delete: "Löschen" };
const describe = (op) => `${OPS[op.op] || op.op} · ${LABELS[op.entityType] || op.entityType || "Änderung"}`;

export default function SyncFailures() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const refresh = () =>
    allFailed().then((a) => setItems(a.sort((x, y) => (y.failedAt || 0) - (x.failedAt || 0)))).catch(() => {});

  useEffect(() => {
    refresh();
    const on = () => refresh();
    window.addEventListener("offline-failed", on);
    window.addEventListener("offline-queued", on);
    return () => {
      window.removeEventListener("offline-failed", on);
      window.removeEventListener("offline-queued", on);
    };
  }, []);

  const retry = async (op) => {
    const { failedAt, failureStatus, lastError, ...rest } = op;
    await addOp({ ...rest, retryCount: 0, status: "pending", nextAttempt: 0, updatedAt: Date.now() });
    await removeFailed(op.operationId);
    await refresh();
    window.dispatchEvent(new Event("offline-queued"));
    if (navigator.onLine) flushQueue();
  };
  const discard = async (op) => { await removeFailed(op.operationId); refresh(); };
  const discardAll = async () => { for (const op of items) await removeFailed(op.operationId); refresh(); };

  if (items.length === 0) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="sync-failures-btn"
        className="ml-2 inline-flex items-center gap-1 underline font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" />
        <span data-testid="sync-failures-count">{items.length}</span> nicht synchronisiert
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="sync-failures-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Nicht synchronisierte Änderungen
            </DialogTitle>
            <DialogDescription>
              Diese offline erfassten Änderungen wurden vom Server abgelehnt und nicht übernommen.
              Sie können sie erneut versuchen oder entfernen.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2" data-testid="sync-failures-list">
            {items.map((op) => (
              <div key={op.operationId} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-900">{describe(op)}</span>
                  <span className="text-xs text-slate-400">
                    {new Date(op.failedAt || op.createdAt).toLocaleString("de-DE")}
                  </span>
                </div>
                <p className="text-xs text-red-600 mt-1 break-words">{op.lastError || `HTTP ${op.failureStatus || ""}`}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => retry(op)} data-testid="sync-retry-btn">
                    <RefreshCw className="h-3 w-3 mr-1" />Erneut versuchen
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => discard(op)} data-testid="sync-discard-btn">
                    <Trash2 className="h-3 w-3 mr-1" />Entfernen
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button size="sm" variant="ghost" className="text-slate-500" onClick={discardAll} data-testid="sync-discard-all-btn">
              Alle entfernen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
